import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listFirmUsers, deactivateFirmUser, closeFirm } from 'services/offboarding';
import { toast } from 'components/Toast';

const ResellerStaff = () => {
    const qc = useQueryClient();
    const [busyId, setBusyId] = useState(null);
    const [closing, setClosing] = useState(false);

    const { data = [], isLoading } = useQuery({
        queryKey: ['firm-users'],
        queryFn: listFirmUsers,
    });

    const users = Array.isArray(data) ? data : (data?.users || data?.items || []);

    const deactivateMut = useMutation({
        mutationFn: ({ userId }) => deactivateFirmUser(userId, {}),
        onSuccess: () => {
            toast('User deactivated');
            qc.invalidateQueries({ queryKey: ['firm-users'] });
        },
        onError: (e) => toast(e?.response?.data?.message || 'Deactivate failed', 'error'),
        onSettled: () => setBusyId(null),
    });

    const handleDeactivate = (u) => {
        const id = u.id || u.userId || u.UserId;
        const name = u.email || u.Email || u.name || id;
        if (!window.confirm(`Deactivate firm user "${name}"? They will lose portal access.`)) return;
        setBusyId(id);
        deactivateMut.mutate({ userId: id });
    };

    const handleCloseFirm = async () => {
        const ok = window.confirm(
            'CLOSE FIRM?\n\nThis closes the reseller/accountant firm account. ' +
            'Client companies are unlinked; current-month billing rules still apply.'
        );
        if (!ok) return;
        const typed = window.prompt('Type CLOSE FIRM to confirm:');
        if ((typed || '').trim().toUpperCase() !== 'CLOSE FIRM') {
            toast('Cancelled — confirmation text did not match.', 'error');
            return;
        }
        setClosing(true);
        try {
            await closeFirm({ confirm: 'CLOSE FIRM' });
            toast('Firm closed');
            localStorage.clear();
            window.location.href = '/login/reseller';
        } catch (err) {
            toast(err?.response?.data?.message || 'Could not close firm.', 'error');
        } finally {
            setClosing(false);
        }
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, gap: 16, flexWrap: 'wrap' }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#1e293b' }}>Staff</h2>
                    <p style={{ margin: '6px 0 0', fontSize: 13, color: '#64748b' }}>
                        Firm users for your reseller portal. Deactivate to revoke access.
                    </p>
                </div>
                <button
                    type="button"
                    disabled={closing}
                    onClick={handleCloseFirm}
                    style={{
                        background: '#fef2f2', color: '#dc2626', border: '1.5px solid #fca5a5',
                        borderRadius: 8, padding: '10px 16px', fontWeight: 700, fontSize: 13,
                        cursor: closing ? 'wait' : 'pointer',
                    }}
                >
                    {closing ? 'Closing…' : 'Close firm'}
                </button>
            </div>

            <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                <div style={{ padding: '14px 18px', borderBottom: '1px solid #f1f5f9', fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
                    Firm users ({users.length})
                </div>
                {isLoading ? (
                    <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>Loading…</div>
                ) : users.length === 0 ? (
                    <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No firm users found.</div>
                ) : (
                    users.map((u) => {
                        const id = u.id || u.userId || u.UserId;
                        const email = u.email || u.Email || '—';
                        const name = u.name || u.fullName || u.FullName || email;
                        const role = u.firmRoleName || u.role || u.Role || u.firmRole || '—';
                        const active = u.isActive !== false && u.active !== false && !(u.deactivatedAt || u.DeactivatedAt);
                        return (
                            <div
                                key={id}
                                style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    padding: '14px 18px', borderBottom: '1px solid #f8fafc', gap: 12, flexWrap: 'wrap',
                                }}
                            >
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: 14, color: '#1e293b' }}>{name}</div>
                                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                                        {email} · {role}
                                        {!active && <span style={{ color: '#dc2626', fontWeight: 600 }}> · Inactive</span>}
                                    </div>
                                </div>
                                {active && (
                                    <button
                                        type="button"
                                        disabled={busyId === id}
                                        onClick={() => handleDeactivate(u)}
                                        style={{
                                            background: '#fff', border: '1px solid #fca5a5', color: '#dc2626',
                                            borderRadius: 7, padding: '6px 12px', fontSize: 12, fontWeight: 600,
                                            cursor: busyId === id ? 'wait' : 'pointer',
                                        }}
                                    >
                                        {busyId === id ? 'Deactivating…' : 'Deactivate'}
                                    </button>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default ResellerStaff;
